// Puzzle days are plain YYYY-MM-DD strings, handled in UTC.

const DAY_MS = 86_400_000;

export function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function parseIsoDate(s: string): Date {
  return new Date(`${s}T00:00:00Z`);
}

export function todayUtc(now: Date = new Date()): string {
  return toIsoDate(now);
}

export function addDays(date: string, days: number): string {
  return toIsoDate(new Date(parseIsoDate(date).getTime() + days * DAY_MS));
}

export function daysBetween(from: string, to: string): number {
  return Math.round((parseIsoDate(to).getTime() - parseIsoDate(from).getTime()) / DAY_MS);
}

export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: string): string {
  const d = parseIsoDate(startOfMonth(date));
  d.setUTCMonth(d.getUTCMonth() + 1);
  return addDays(toIsoDate(d), -1);
}

export function addMonths(date: string, months: number): string {
  const d = parseIsoDate(startOfMonth(date));
  d.setUTCMonth(d.getUTCMonth() + months);
  return toIsoDate(d);
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayMon0(date: string): number {
  return (parseIsoDate(date).getUTCDay() + 6) % 7;
}

/**
 * A day's puzzle is published once that date has begun in the first time zone (UTC+14).
 */
export function isPublishedDay(date: string, now: Date = new Date()): boolean {
  return parseIsoDate(date).getTime() - 14 * 3_600_000 <= now.getTime();
}

const monthFmt = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
const shortFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const longFmt = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const dateTimeFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
  hour12: false,
});

export const formatMonth = (date: string) => monthFmt.format(parseIsoDate(date));
export const formatShortDate = (date: string) => shortFmt.format(parseIsoDate(date));
export const formatLongDate = (date: string) => longFmt.format(parseIsoDate(date));
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${dateTimeFmt.format(d)} UTC`;
}

/** The ISO week (UTC) containing `date`, e.g. "2026-W39": the week's year is its Thursday's. */
export function isoWeekOf(date: string): string {
  const thursday = new Date(parseIsoDate(date).getTime() + (3 - weekdayMon0(date)) * DAY_MS);
  const year = thursday.getUTCFullYear();
  const week = 1 + Math.floor((thursday.getTime() - Date.UTC(year, 0, 1)) / (7 * DAY_MS));
  return `${year}-W${String(week).padStart(2, "0")}`;
}

/** The Monday (YYYY-MM-DD) of an ISO week such as "2026-W41"; null for anything else. */
export function isoWeekMonday(week: string): string | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!m) return null;
  const jan4 = `${m[1]}-01-04`;
  return addDays(jan4, -weekdayMon0(jan4) + (Number(m[2]) - 1) * 7);
}

/** "2026-W41" → "5 Oct – 11 Oct" (Monday to Sunday); the week itself for anything else. */
export function isoWeekSpan(week: string): string {
  const monday = isoWeekMonday(week);
  return monday ? `${formatShortDate(monday)} – ${formatShortDate(addDays(monday, 6))}` : week;
}

/** The ISO weeks ending with `week` (inclusive), newest first. */
export function recentIsoWeeks(week: string, count: number): string[] {
  const monday = isoWeekMonday(week);
  if (!monday) return [];
  return Array.from({ length: count }, (_, i) => isoWeekOf(addDays(monday, -7 * i)));
}
